import type { PropsWithChildren } from "react";
import { useMemo } from "react";
import { CampingRoute } from "../../router/CampingRoute";
import { PICTURES } from "../Photos/pictures";
import LogoBeauce from "./beauce.png";
import LogoFQCC from "./fqcc.png";

const Root = ({ children }: PropsWithChildren) => (
  <div className="min-h-screen px-4 py-8 text-center sm:px-6 sm:py-12">
    {children}
  </div>
);

const Logos = ({ children }: PropsWithChildren) => (
  <div className="mt-10 flex flex-wrap items-center justify-center gap-10 sm:mt-12 sm:gap-16">
    {children}
  </div>
);

const Text = ({ children }: PropsWithChildren) => (
  <div className="mx-auto mt-8 max-w-3xl space-y-6 text-base leading-relaxed text-slate-700 sm:mt-12 sm:text-lg">
    {children}
  </div>
);

const pickRandomPictures = (count: number) => {
  const shuffled = [...PICTURES];

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const swapIndex = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[i]];
  }

  return shuffled.slice(0, count);
};

const GalleryStrip = () => {
  const featuredPictures = useMemo(() => pickRandomPictures(4), []);

  return (
    <div className="mx-auto mt-8 grid max-w-5xl grid-cols-2 gap-3 sm:mt-10 sm:grid-cols-4 sm:gap-4">
      {featuredPictures.map((picture, index) => (
        <div
          key={picture.thumbnail}
          className="overflow-hidden rounded-2xl border border-emerald-100 bg-white/80 shadow-sm"
        >
          <img
            src={picture.thumbnail}
            alt={`Camping ${index + 1}`}
            className="h-28 w-full object-cover sm:h-32"
          />
        </div>
      ))}
    </div>
  );
};

function Home() {
  return (
    <Root>
      <header className="mx-auto max-w-4xl">
        <p className="text-xs font-semibold uppercase tracking-[0.4em] text-emerald-700 sm:text-sm">
          Camping des Îles
        </p>
        <h1 className="mt-4 text-2xl font-semibold text-slate-900 sm:text-4xl">
          Séjour au Lac des Îles
        </h1>
        <Text>
          <p className="mt-4 text-base text-slate-600 sm:text-lg">
            Situé à St-Hilaire-de-Dorset en Beauce, notre camping offre un cadre
            paisible et agréable au cœur d'un très beau paysage.
          </p>
        </Text>
      </header>

      <GalleryStrip />

      <Text>
        <div className="grid gap-4 text-left sm:grid-cols-2">
          <div className="rounded-2xl border border-emerald-100 bg-white/80 p-5 shadow-sm">
            <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">
              Pêche
            </p>
            <p className="mt-2">
              Pour les amateurs, la truite mouchetée est au rendez-vous.
            </p>
          </div>
          <div className="rounded-2xl border border-amber-100 bg-white/80 p-5 shadow-sm">
            <p className="text-sm font-semibold uppercase tracking-wide text-amber-700">
              Terrains
            </p>
            <p className="mt-2">
              50 terrains, boisés ou non, pour s'installer à son rythme.
            </p>
          </div>
          <div className="rounded-2xl border border-sky-100 bg-white/80 p-5 shadow-sm">
            <p className="text-sm font-semibold uppercase tracking-wide text-sky-700">
              Famille
            </p>
            <p className="mt-2">
              Une piscine et un terrain de jeux pour les enfants.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-white/80 p-5 shadow-sm">
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-700">
              Services
            </p>
            <p className="mt-2">
              Bloc sanitaire et buanderie disponibles 24 heures sur 24.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-6 text-left">
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">
            Sur le lac
          </p>
          <p className="mt-2">
            Location d'embarcations nautiques (pédalo, chaloupe, kayak) pour vos
            promenades sur le lac.
          </p>
        </div>

        <div className="rounded-2xl p-6 text-left">
          <p>
            Venez profiter de vos vacances et découvrir notre région. Ça vaut le
            détour!
          </p>
          <p className="mt-4 font-semibold text-slate-900">
            Bienvenue à tous et bonnes vacances
          </p>
          <p className="mt-2 italic text-slate-600">Véronique et Jasmin</p>
        </div>
      </Text>
      <Logos>
        <a href="http://www.destinationbeauce.com/">
          <img
            alt="Logo Membre de tourisme Beauce-Sartigan"
            className="h-20 w-auto sm:h-24"
            src={LogoBeauce}
            title="Membre de tourisme Beauce-Sartigan"
          />
        </a>

        <a href="http://www.fqcc.ca/">
          <img
            alt="Logo FQCC"
            className="h-16 w-auto sm:h-20"
            src={LogoFQCC}
            title="FQCC"
          />
        </a>
      </Logos>
    </Root>
  );
}

export const HomeRoute: CampingRoute = {
  title: "Acceuil",
  path: "/",
  element: <Home />,
};
